import { useEffect, useState } from "react";

/**
 * Lightweight cross-subdomain session detection.
 *
 * The product app at app.pipeguide.tech sets a NON-httpOnly hint cookie:
 *     pg_logged_in=1; Domain=.pipeguide.tech; Path=/; Secure; SameSite=Lax
 * (and clears it on logout).
 *
 * The marketing site can read that cookie from document.cookie because the
 * Domain attribute is the parent zone. This is a *hint only* — it does NOT
 * grant authentication. Real auth tokens stay httpOnly on app.pipeguide.tech.
 *
 * Usage:
 *   const { loggedIn } = useSession();
 *   if (loggedIn) ...
 */
const COOKIE_NAME = "pg_logged_in";

function readCookie(name) {
    if (typeof document === "undefined") return null;
    const match = document.cookie
        .split(";")
        .map((c) => c.trim())
        .find((c) => c.startsWith(name + "="));
    return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

export function useSession() {
    const [loggedIn, setLoggedIn] = useState(false);

    useEffect(() => {
        const check = () => setLoggedIn(readCookie(COOKIE_NAME) === "1");
        check();
        // Re-check when tab regains focus (covers login-in-another-tab → return).
        const onFocus = () => check();
        window.addEventListener("focus", onFocus);
        // Also poll lightly so log-in/out reflects within a few seconds.
        const id = setInterval(check, 5000);
        return () => {
            window.removeEventListener("focus", onFocus);
            clearInterval(id);
        };
    }, []);

    return { loggedIn };
}
